import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref, shallowRef, toRaw, triggerRef } from "vue";
import { DateTime } from "luxon";

import { cache, CacheClosedError, sectorHistory } from "@/services/cache";

import { useApplicationStore } from "@/stores/Application";
import { createInitLatch } from "@/stores/init-latch";
import { useAuthStore } from "@/stores/Auth";
import { useSectorsStore } from "@/stores/Sectors";
import {
    bpiStatusContextAfter,
    rehydrateFromLogReturns,
    hydrate,
    refreshFromServer
} from "@/services/data/sync/sector-history-sync-coordinator";
import type { BpiStatusContext } from "@/services/data/sync/sector-history-sync-coordinator";
import { Unauthorized } from "@tmi/types/api/responses";

import type { Tuple } from "@tmi/types/adts";
import type { CompositionContext } from "@/services/data/sync-supervisor";
import {
    NormalizedPersistedSectorsState,
    NormalizedSectorMarketState,
    SectorAlert,
    SectorMarketState,
    SectorMarketStateContainer,
    SectorMarketStateValues
} from "@tmi/types/entities";
import { normalizeDateTime } from "@tmi/utils/date-time";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

import { clientStateManager } from "@/services/error-services";
import { useLocalStorage } from "@vueuse/core";

const DEFAULT_TIMEOUT_MS = 30000; // 30 seconds

function withTimeout<T>( promise: Promise<T>, timeoutMs: number = DEFAULT_TIMEOUT_MS, operationName: string = 'Operation' ): Promise<T> {
    return new Promise<T>( ( resolve, reject ) => {
        const timeoutId = setTimeout( () => {
            reject( new Error( `${ operationName } timed out after ${ timeoutMs }ms` ) );
        }, timeoutMs );

        promise
            .then( ( result ) => {
                clearTimeout( timeoutId );
                resolve( result );
            } )
            .catch( ( err ) => {
                clearTimeout( timeoutId );
                reject( err );
            } );
    } );
}

// Why the last initialization failed (its message), for a view's error state. Cleared when a load starts and by reset().
const error = ref< string | undefined >();
const initialized = ref< boolean >( false );
const fetching = ref< boolean >( false );
const loading = ref< boolean >( false );

// The load in flight, joined by every caller that arrives while it runs. A plain `loading` guard
// resolved those callers immediately against an empty store: a deep-linked Sector Details flyout
// mounting during the sync-driven reload snapshotted an empty series and never recovered (tmi-e7w.65).
let initialization: Promise< void > | null = null;

// tmi-7be: a cache failure stops initialization until reset(). SectorBellCurve.vue re-runs setSelectedDate() ->
// initializeAsync() whenever isLoading turns false; without this latch an immediately-rejecting cache (eviction, or
// storage unavailable) spun that pair forever in microtasks and froze the tab.
const initLatch = createInitLatch();

const isLoading = computed< boolean >( () => loading.value );
// Rank 41: a load has finished (from the cache or the server). Lets a view tell "history not loaded yet" from "this
// sector has no history" once isLoading is false; false again after reset() and after a failed load.
const isInitialized = computed< boolean >( () => initialized.value );

// Rank 65: shallow. A deep ref proxied every nested Map and row of the whole history (every day since 2019 x 45
// sectors), so the first read after load or after a sync re-read cost ~0.6 s on desktop. The history is only ever
// replaced (applySnapshot) or mutated in place followed by triggerRef (setSelectedDate); rows are never edited in place.
const sectorHistoryRepository =
    shallowRef< Map< string, Map< number, SectorMarketState > > >( new Map< string, Map< number, SectorMarketState > >() );

// The metric series start after this day (exclusive).
const SERIES_START_DATE = '2018-01-01';
const SORTABLE_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// A real calendar day in `yyyy-MM-dd` form, checked without luxon: such keys order correctly as strings.
function isSortableCalendarDate( dt: string ): boolean {
    const parts = SORTABLE_DATE.exec( dt );
    if ( !parts ) {
        return false;
    }

    const [ year, month, day ] = [ Number( parts[ 1 ] ), Number( parts[ 2 ] ), Number( parts[ 3 ] ) ];
    const date = new Date( Date.UTC( year, month - 1, day ) );
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

const allSectorAlertsUniquenessTracker = new Set< string >();
const allSectorAlerts = ref< SectorAlert[] >( [] );

const selectedDate = ref< string >( '' );

// DP-22 (tmi-e7w.22): days setSelectedDate fetched from the server because the cache did not hold them, kept as
// fetched. A re-read of the cache puts them back instead of dropping them. They live only in memory (never
// persisted) and are never the previous day of another day: not a BPI status seed, not an EW index / RS base.
const onDemandDays = new Map< string, NormalizedPersistedSectorsState >();

// DP-22: the sync-driven re-read in flight, and whether another sync asked for one while it ran. Every caller joins
// the same run, which reads the cache again once for all the requests that arrived during a read.
let reload: Promise< void > | null = null;
let reloadRequested = false;

// DP-22: bumped by reset(). A cache read or server fetch that started before a reset (sign-out, account switch)
// must not apply its result after it.
let generation = 0;

/** A complete in-memory history, built off to the side and then assigned in one step. */
interface SectorHistorySnapshot {
    repository: Map< string, Map< number, SectorMarketState > >;
    alerts: SectorAlert[];
    alertKeys: Set< string >;
}

// The server's latest market date as of boot or sign-in (Application.refreshDateOfMostRecentMarketDataAvailable). DP-11:
// only the fallback for `latestMarketDate` while no sector-history day is synced (a cold cache).
const dateOfMostRecentMarketDataAvailable = useLocalStorage< string >( 'latest-dt', '' );

// DP-11 (tmi-e7w.11): whether the selected date follows `latestMarketDate`. True until someone selects any other date;
// selecting the latest date again resumes following. reset() restores it.
let followingLatest = true;


export const useSectorHistoryStore = defineStore( "sector-history", () => {
    // Depends on authStore for authentication state and appStore to signal that data is being loaded
    const appStore = useApplicationStore();
    const { authToken } = storeToRefs( useAuthStore() );
    // DP-22: the Sector Details flyout reads its row from here; a rebuilt history re-points it.
    const sectorsStore = useSectorsStore();

    // The newest synced day in memory. Days fetched on demand are left out (DP-11): they are picks, not sync results.
    const latestMarketDataRetrieved = computed< string | undefined >( () => {
        const dates = Array.from( sectorHistoryRepository.value.keys() )
            .filter( ( dt: string ) => !onDemandDays.has( dt ) )
            .sort( ( d1: string, d2: string ) => d2.localeCompare( d1 ) );

        return dates.length > 0 ? dates[ 0 ] : undefined;
    } );

    /**
     * DP-11 (tmi-e7w.11): the one latest market date every date-bound surface uses: the newest synced sector-history
     * day, which moves whenever a sync writes a newer day and the store re-reads. `latest-dt` answers only while nothing
     * is synced (a cold cache), so a stale or skewed `latest-dt` can no longer put one surface on another date.
     */
    const latestMarketDate = computed< string >( () => latestMarketDataRetrieved.value ?? dateOfMostRecentMarketDataAvailable.value );

    // Clear all internal tracking and state variables
    function reset() {
        error.value = undefined;
        initialized.value = false;
        loading.value = false;
        fetching.value = false;
        // A reset means IDB is about to be re-read; the next initializeAsync must start a fresh load
        // rather than join one that may already have read the old contents.
        initialization = null;
        reload = null;
        reloadRequested = false;
        generation += 1;
        followingLatest = true;
        onDemandDays.clear();
        initLatch.clear();
        selectedDate.value = '';
        allSectorAlerts.value = [];
        allSectorAlertsUniquenessTracker.clear();

        resetSectorHistory();
    }

    function initializeAsync(): Promise<void> {
        if ( initialized.value || initLatch.isHalted() ) {
            return Promise.resolve();
        }

        if ( initialization ) {
            logger.debug( '[ Sector-History ].initialize() joining the load already in flight' );
            return initialization;
        }

        const run: Promise< void > = runInitialization().finally( () => {
            if ( initialization === run ) {
                initialization = null;
            }
        } );
        initialization = run;

        return run;
    }

    async function runInitialization(): Promise<void> {
        const runGeneration = generation;
        loading.value = true;
        // A retry is loading again, not failed: views show their skeleton until it settles
        error.value = undefined;

        try {
            // Start on the latest market date, unless a date is already selected (DP-22: a load never moves the
            // user off a date they picked). With nothing synced yet this is `latest-dt`; the loaded history below
            // moves a following selection to the newest synced day (DP-11).
            if ( !selectedDate.value ) {
                selectedDate.value = latestMarketDate.value;
            }

            // Wait for the database to open before the first read.
            await withTimeout( cache.ready(), DEFAULT_TIMEOUT_MS, 'IndexedDB ready' );

            if ( !await sectorHistory.isEmpty() ) {
                const loadedStateData: NormalizedPersistedSectorsState[] = await sectorHistory.all();
                if ( runGeneration !== generation ) {
                    return;
                }

                applySnapshot( buildSnapshot( loadedStateData ) );
                initialized.value = true;
            } else {
                // A cold cache: fetch the latest market date. The sync supervisor fills the cache only for a member
                // whose tiers grant both the `sectors` and `alerts` features, so for everyone else this fetch is the one
                // source of the day the Top 10% Strongest Sectors and Sector Stock Leaders cards read
                // (mostRecentSectorData holds synced days only, DP-11). The guard used to read the newest synced day,
                // which an empty cache never has, so the fetch never ran and those cards stayed blank for such a member
                // once DP-11 stopped them reading the day the bell curve fetched on demand (tmi-prime v3.5.73).
                // `latest-dt` is the server's latest market date as of boot or sign-in.
                const startingDate: string = dateOfMostRecentMarketDataAvailable.value;

                if ( authToken.value && startingDate ) {
                    const sectorStateData = await withTimeout(
                        refreshFromServer( authToken.value, [ startingDate ] ),
                        DEFAULT_TIMEOUT_MS,
                        'Sector history fetch'
                    );
                    if ( runGeneration !== generation ) {
                        return;
                    }

                    applySnapshot( buildSnapshot( sectorStateData ) );
                    initialized.value = true;
                } else {
                    // No auth token or no latest data available — mark as initialized anyway to prevent stuck state
                    logger.debug( '[ Sector-History ].initialize() - No auth token or latest data, marking initialized' );
                    initialized.value = true;
                }
            }
        } catch ( err: any ) {
            // A reset() while this run was reading: its successor owns the store now.
            if ( runGeneration !== generation ) {
                return;
            }

            // A newer build evicted this context: stop quietly, the reload is already in flight.
            if ( err instanceof CacheClosedError ) {
                initLatch.halt( err );
                return;
            }

            reset();
            initLatch.halt( err );   // after reset(), which clears it: a cache failure stays latched
            // After reset() too, which clears it. Without it a failed load left isLoading and isInitialized false and
            // no error, so a view's AsyncState read "still loading" and showed its skeleton for good.
            error.value = err instanceof Error ? err.message : String( err );
            clientStateManager.queueClientError( err, {
                token: authToken.value
            } );
        } finally {
            // reset() already cleared the flag, and a later run may have set it again.
            if ( runGeneration === generation ) {
                loading.value = false;
            }
        }
    }

    /**
     * DP-22 (tmi-e7w.22): re-reads the cache after the sync worker wrote sector history, without emptying the store.
     *
     * This replaces `reset()` + `initializeAsync()`, which cleared the history, the alerts and the selected date for
     * the whole read: every sector surface went blank, a picked date snapped back to `latest-dt`, and days fetched on
     * demand were lost. Now the new history is built off to the side and assigned in one step, the selected date is
     * kept, and `isLoading` does not change (it means "no history yet", and it unmounts the Sector Details content).
     *
     * Joins a first load in flight (which may have read the cache before the write) and then reads again. Requests
     * that arrive during a read coalesce into one more read. A failed read keeps the current history.
     */
    function reloadFromCacheAsync(): Promise< void > {
        reloadRequested = true;
        if ( !reload ) {
            const run: Promise< void > = runReloads().finally( () => {
                if ( reload === run ) {
                    reload = null;
                }
            } );
            reload = run;
        }

        return reload;
    }

    async function runReloads(): Promise< void > {
        const runGeneration = generation;
        while ( reloadRequested && runGeneration === generation ) {
            reloadRequested = false;
            await runReload();
        }
    }

    async function runReload(): Promise< void > {
        if ( initialization ) {
            await initialization;
        }

        if ( !initialized.value ) {
            // Never loaded, or the last load failed. An evicted tab is already reloading. Otherwise the worker just
            // wrote the cache, so a failed read gets one more try (reset() cleared the latch here before DP-22). This
            // cannot loop: only a sync message starts a re-read, never a watcher on this store.
            if ( initLatch.halted.value === 'evicted' ) {
                return;
            }
            initLatch.clear();
            await initializeAsync();
            return;
        }

        const runGeneration = generation;
        try {
            await withTimeout( cache.ready(), DEFAULT_TIMEOUT_MS, 'IndexedDB ready' );
            const loadedStateData: NormalizedPersistedSectorsState[] = await sectorHistory.all();
            if ( runGeneration !== generation ) {
                return;
            }

            applySnapshot( buildSnapshot( loadedStateData ) );
        } catch ( err: any ) {
            if ( runGeneration !== generation ) {
                return;
            }

            // A newer build evicted this context: stop quietly and keep what is shown until the page reloads.
            if ( err instanceof CacheClosedError ) {
                initLatch.halt( err );
                return;
            }

            // The history in memory is still whole; the next sync that writes asks for another read.
            clientStateManager.queueClientError( err, {
                token: authToken.value
            } );
        }
    }

    /**
     * DP-22: builds a complete history from cached (or fetched) days without touching the store, so the swap is a
     * single assignment. Alerts are rebuilt from scratch, so a revised alert replaces its cached copy rather than
     * being skipped as a duplicate. Days fetched on demand that the cache still lacks are put back after the cached
     * days are built, so they never feed a cached day; once the cache holds a day, its cached copy wins.
     */
    function buildSnapshot( sectorStateData: NormalizedPersistedSectorsState[] ): SectorHistorySnapshot {
        rehydrateFromLogReturns( sectorStateData );

        const repository = new Map< string, Map< number, SectorMarketState > >();
        const alerts: SectorAlert[] = [];
        const alertKeys = new Set< string >();
        sectorStateData.forEach( ( state: NormalizedPersistedSectorsState ) => {
            state.sectors.forEach( ( sectorState: NormalizedSectorMarketState ) => {
                if ( sectorState.alerts && sectorState.alerts.length > 0 ) {
                    for ( const alert of sectorState.alerts ) {
                        const alertKey = `${ alert.dt }_${ alert.sector_id }_${ alert.typeId }`;
                        if ( !alertKeys.has( alertKey ) ) {
                            alertKeys.add( alertKey );
                            alerts.push( alert );
                        }
                    }
                }
                // tmi-buu: bpi.stat arrives already derived by the sync
                // coordinator's chronological status pass — the store no
                // longer recomputes it.
            });

            const hydratedResult = hydrate( state, appStore.compositionCtx );
            repository.set( hydratedResult.item1, hydratedResult.item2 );
        } );

        alerts.sort( ( sa1: SectorAlert, sa2: SectorAlert ) => sa2.dt.localeCompare( sa1.dt ) );

        for ( const dt of [ ...onDemandDays.keys() ] ) {
            if ( repository.has( dt ) ) {
                onDemandDays.delete( dt );
            }
        }
        for ( const day of onDemandDays.values() ) {
            hydrateFromContainer( repository, day, appStore.compositionCtx );
        }

        return { repository, alerts, alertKeys };
    }

    function applySnapshot( snapshot: SectorHistorySnapshot ): void {
        sectorHistoryRepository.value = snapshot.repository;
        allSectorAlertsUniquenessTracker.clear();
        snapshot.alertKeys.forEach( key => allSectorAlertsUniquenessTracker.add( key ) );
        allSectorAlerts.value = snapshot.alerts;

        // DP-11: a selection that follows the latest date moves with it; a picked date stays.
        if ( followingLatest && latestMarketDate.value ) {
            selectedDate.value = latestMarketDate.value;
        }

        repointSelectedSector();
    }

    /**
     * DP-22 (d): the Sector Details flyout holds a row object from the previous history. Point it at the rebuilt row
     * for the same sector and date, so its tiles show the synced values and its identity watch rebuilds the series.
     * A flyout whose date the rebuilt history lacks keeps its row.
     */
    function repointSelectedSector(): void {
        const open: SectorMarketState | undefined = sectorsStore.selectedSector;
        if ( !open ) {
            return;
        }

        // The history holds raw rows (rank 65); the sectors store hands back its reactive proxy of the open one.
        const rebuilt = sectorHistoryRepository.value.get( open.dt )?.get( open.sector.id );
        if ( rebuilt && rebuilt !== toRaw( open ) ) {
            sectorsStore.setSelectedSector( rebuilt );
        }
    }

    const sectorStateForSelectedDate = computed< SectorMarketStateContainer >( () => {
        let sectorData: SectorMarketState[] = [];

        const currentlySelectedDate = selectedDate.value;
        if ( !!currentlySelectedDate && sectorHistoryRepository.value.has( currentlySelectedDate ) ) {
            const repositoryDateForSelectedDate: Map< number, SectorMarketState > | undefined = sectorHistoryRepository.value.get( currentlySelectedDate );
            if ( repositoryDateForSelectedDate ) {
                sectorData = Array.from( repositoryDateForSelectedDate.values() );
            }
        }

        return {
            date: selectedDate.value,
            history: sectorData
        };
    } );

    /**
     * Rank 65: the days every metric series spans, oldest first, parsed once per history change. Days whose key is not a
     * valid date are left out, and only days after SERIES_START_DATE count. Every synced key is `yyyy-MM-dd`, so the
     * window is a string compare; luxon parses only when some key is in another format. Nothing is served while there
     * is no valid latest market date.
     */
    const seriesDates = computed< string[] >( () => {
        const repository = sectorHistoryRepository.value;
        // Rank 41: gated on DP-11's latestMarketDate (the newest synced day, else `latest-dt`), not on `latest-dt`
        // alone. That key stays '' when its one request failed on a first visit, and every BPI and Sector Details
        // chart then came up blank although the history was cached. normalizeDateTime answers null for ''.
        const latest = latestMarketDate.value;
        if ( !latest || !normalizeDateTime( latest )?.isValid ) {
            return [];
        }

        const keys = [ ...repository.keys() ];
        if ( keys.every( isSortableCalendarDate ) ) {
            return keys.filter( dt => dt > SERIES_START_DATE ).sort();
        }

        const initialDate = normalizeDateTime( SERIES_START_DATE );
        return keys
            .map( dt => ( { dt, parsed: normalizeDateTime( dt ) } ) )
            .filter( ( { parsed } ) => !!parsed?.isValid && parsed > initialDate )
            .sort( ( d1, d2 ) => d1.parsed.toMillis() - d2.parsed.toMillis() )
            .map( ( { dt } ) => dt );
    } );

    // One sector's series, built on first request and kept until the history changes. The whole map used to be rebuilt
    // for all 45 sectors whenever anything read it, although the flyout and All BPIs each need one sector.
    const seriesBySector = computed< Map< number, SectorMarketStateValues[] > >( () => {
        void seriesDates.value;
        return new Map< number, SectorMarketStateValues[] >();
    } );

    /**
     * Rank 65: the metric history of one sector, oldest day first. Reactive: a computed that calls it re-runs when the
     * history changes. Returns an empty array for a sector with no history.
     */
    function sectorSeries( sectorId: number ): SectorMarketStateValues[] {
        const cache = seriesBySector.value;
        const cached = cache.get( sectorId );
        if ( cached ) {
            return cached;
        }

        const repository = sectorHistoryRepository.value;
        const series: SectorMarketStateValues[] = [];
        for ( const dt of seriesDates.value ) {
            const sectorMetricsForDate = repository.get( dt )?.get( sectorId );
            if ( !sectorMetricsForDate ) {
                continue;
            }

            // val and delta are already rehydrated from log returns (buildSnapshot, hydrateFromContainer)
            series.push( {
                dt: sectorMetricsForDate.dt,
                idx: sectorMetricsForDate.idx,
                bpi: sectorMetricsForDate.bpi,
                p30: sectorMetricsForDate.p30,
                p10: sectorMetricsForDate.p10,
                rs: sectorMetricsForDate.rs,
                alerts: sectorMetricsForDate.alerts
            } );
        }

        cache.set( sectorId, series );
        return series;
    }

    async function setSelectedDate( selectedDateAsSortableString: string ): Promise<void> {
        await initializeAsync();

        // A latched store is either evicted (a reload is in flight) or has no working cache. Going on to the server
        // fetch would reach its catch, whose reset() clears the latch and re-arms the watcher loop (tmi-7be).
        if ( initLatch.isHalted() || !selectedDateAsSortableString ) {
            return;
        }

        logger.debug( `selectedDateAsSortableString: ${ selectedDateAsSortableString }, alreadyAvailable: ${ sectorHistoryRepository.value.has( selectedDateAsSortableString ) }` );

        if ( !authToken.value ) {
            throw new Unauthorized();
        }

        // DP-11: any date other than the latest is a pick that later syncs leave alone.
        followingLatest = selectedDateAsSortableString === latestMarketDate.value;

        if ( sectorHistoryRepository.value.has( selectedDateAsSortableString ) ) {
            // Data already loaded
            selectedDate.value = selectedDateAsSortableString;
            return;
        }

        fetching.value = true;
        logger.debug( `Date ${ selectedDateAsSortableString } is not currently in memory, fetching...` );

        const fetchGeneration = generation;
        try {
            const sectorHistoryData = await withTimeout(
                refreshFromServer(
                    authToken.value,
                    [ selectedDateAsSortableString ],
                    buildSeedContextFromRepository( selectedDateAsSortableString )
                ),
                DEFAULT_TIMEOUT_MS,
                'Sector history fetch for date'
            );

            if ( fetchGeneration !== generation ) {
                return;
            }

            if ( sectorHistoryData?.length > 0 ) {
                for ( const marketData of sectorHistoryData ) {
                    onDemandDays.set( marketData.dt, marketData );
                    hydrateFromContainer( sectorHistoryRepository.value, marketData, appStore.compositionCtx );
                }
                // The repository is a shallowRef mutated in place above (rank 65).
                triggerRef( sectorHistoryRepository );
            }
        } catch ( err: any ) {
            // DP-22 (f): this used to reset() the store, wiping every cached day in memory over one missing date.
            if ( fetchGeneration === generation ) {
                clientStateManager.queueClientError( err, {
                    token: authToken.value,
                    selectedDate: selectedDateAsSortableString
                } );
            }
        } finally {
            if ( fetchGeneration === generation ) {
                selectedDate.value = selectedDateAsSortableString;
                fetching.value = false;
            }
        }
    }

    const mostRecentSectorData = computed< SectorMarketState[] >( () => {
        let latestSectorHistory: SectorMarketState[] = [];

        // DP-11: the rows of the one latest market date (the newest synced day), never of a day fetched on demand.
        const mostRecentDate: string | undefined = latestMarketDataRetrieved.value;
        if ( mostRecentDate ) {
            latestSectorHistory = Array.from( sectorHistoryRepository.value.get( mostRecentDate )!.values() );
        }

        return latestSectorHistory;
    } );

    function resetSectorHistory(): void {
        // Replaced, not cleared: a shallowRef does not see an in-place clear() (rank 65).
        sectorHistoryRepository.value = new Map< string, Map< number, SectorMarketState > >();
    }

    // Adds one day fetched on its own to `repository`, compounding its EW index and RS from the nearest earlier
    // cached day (DP-22: never from another day fetched on demand).
    function hydrateFromContainer(
        repository: Map< string, Map< number, SectorMarketState > >,
        entity: NormalizedPersistedSectorsState,
        ctx: CompositionContext
    ): void {
        const result: Tuple< string, Map< number, SectorMarketState > > = hydrate( entity, ctx );

        // Rehydrate single-date fetch: compute val from the previous date's rehydrated val + this date's log_rtn
        const previousDate = [ ...repository.keys() ]
            .filter( dt => dt < result.item1 && !onDemandDays.has( dt ) )
            .sort( ( a, b ) => b.localeCompare( a ) )[ 0 ];

        for ( const [ sectorId, sectorState ] of result.item2 ) {
            const previousSectorState = previousDate
                ? repository.get( previousDate )?.get( sectorId )
                : undefined;

            // EW Index: val = previousVal * e^(log_rtn), or base * e^(log_rtn) if no prior data
            if ( sectorState.idx ) {
                const prevIdxVal = previousSectorState?.idx?.val;
                sectorState.idx.val = prevIdxVal != null
                    ? prevIdxVal * Math.exp( sectorState.idx.log_rtn )
                    : 1000 * Math.exp( sectorState.idx.log_rtn );
                sectorState.idx.delta = prevIdxVal != null
                    ? sectorState.idx.val - prevIdxVal
                    : undefined;
            }

            // RS: val = previousVal * e^(log_rtn), or base * e^(log_rtn) if no prior data
            const prevRsVal = previousSectorState?.rs?.val;
            sectorState.rs.val = prevRsVal != null
                ? prevRsVal * Math.exp( sectorState.rs.log_rtn )
                : 1000 * Math.exp( sectorState.rs.log_rtn );
        }

        repository.set( result.item1, result.item2 );
    }

    // tmi-buu: builds the BPI status machine's seed for a single-date fetch —
    // the nearest earlier date already in memory. DP-09: the seed carries that
    // day's status, which the writer's rule retains until a column or signal flip.
    // The sync worker folds against the cached days instead. DP-22: a day fetched on demand is never a seed.
    function buildSeedContextFromRepository( beforeDate: string ): Map< number, BpiStatusContext > | undefined {
        const seedDate = [ ...sectorHistoryRepository.value.keys() ]
            .filter( dt => dt < beforeDate && !onDemandDays.has( dt ) )
            .sort( ( d1, d2 ) => d2.localeCompare( d1 ) )[ 0 ];
        if ( !seedDate ) {
            return undefined;
        }

        const seed = new Map< number, BpiStatusContext >();
        for ( const [ tmiSectorId, sectorState ] of sectorHistoryRepository.value.get( seedDate )!.entries() ) {
            seed.set( tmiSectorId, bpiStatusContextAfter( sectorState.bpi ) );
        }

        return seed;
    }

    return {
        // ACTIONS
        initializeAsync,
        reloadFromCacheAsync,
        reset,
        setSelectedDate,

        // GETTERS
        allSectorAlerts,
        error,
        isInitialized,
        isLoading,
        latestMarketDataRetrieved,
        latestMarketDate,
        mostRecentSectorData,
        sectorSeries,
        sectorStateForSelectedDate
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSectorHistoryStore, import.meta.hot ) );
}
